import {
  calculateAverageSimilarity,
  calculatePathDistance,
  type BacktrackReportEntry,
  type MatchVerdict,
  type MoveJudgement,
  type PotentialRarestMove,
  type RoutePathFn,
  type SeatStatus,
} from "@synapse/engine";

import type { Achievement } from "../features/achievements";
import type { GraphData } from "../services/dataLoader";

// Move evaluation and distance helpers live in @synapse/engine (shared with tools/arena).
export type {
  BacktrackReportEntry,
  MoveJudgement,
  PotentialRarestMove,
  RoutePathFn,
} from "@synapse/engine";
export {
  calculateAverageSimilarity,
  calculatePathDistance,
  calculateTotalSemanticDistance,
  getSemanticDistance,
  judgeMove,
  nextOnPath,
} from "@synapse/engine";

/**
 * One move as the player made it. moveHistory keeps every move, including
 * moves a backtrack later undid; optimalChoices keeps only the surviving path.
 */
export interface MoveRecord extends MoveJudgement {
  /**
   * Hops of the Local Optimal route from the chosen word to the target (0 on
   * it, -1 unreachable). Persisted key; Ruling R5.
   */
  hopsFromChoiceToEnd?: number;
  /** Frequency of the rarest word offered at this step, when frequencies are known. */
  rarestOfferedFrequency?: number;
}

/**
 * A finished game's report. Persisted and legacy key names are kept (Ruling
 * R8); their technical terms:
 * - optimalPath: the gold path (best route from start to target, fixed at game start).
 * - suggestedPath: the Local Optimal route from the final word (empty on a win).
 * - optimalMovesMade: moves that were Local Optimal (which includes Global Optimal).
 * - optimalChoices: the MoveJudgement of each move on the surviving path.
 * - missedOptimalMoves: where a move left the gold path, as text.
 * - optimalSemanticDistance: the gold path's distance.
 * - pathEfficiency: par (gold path hops) / moves.
 */
/** Her side of a finished match (spec §3.2), on every report of a game she played. */
export interface AriadneReport {
  verdict: MatchVerdict;
  status: SeatStatus;
  moves: number;
  turns: number;
  backtracks: number;
  /** Her learned-games counter after this game. */
  games: number;
  hash: string;
  /** She followed the player's every action (the tutorial): no race, no verdict, no line. */
  followed?: true;
}

export interface GameReport {
  playerPath: string[];
  optimalPath: string[];
  suggestedPath: string[];
  optimalMovesMade: number;
  totalMoves: number;
  moveAccuracy: number;
  optimalChoices: MoveJudgement[];
  /** Every move made, in order, backtracked ones included. Absent on older reports. */
  moveHistory?: MoveRecord[];
  missedOptimalMoves: string[];
  playerSemanticDistance: number;
  optimalSemanticDistance: number;
  averageSimilarity: number | null;
  backtrackEvents?: BacktrackReportEntry[]; // Renamed and using new type
  /** Reports rebuilt from history keep the backtrack count, not the events. */
  backtrackCount?: number;
  earnedAchievements?: Achievement[]; // Added to store earned achievements
  /** Earned achievements unlocked for the first time by this game. */
  newAchievementIds?: string[];
  potentialRarestMoves?: PotentialRarestMove[]; // Added for "Putting on the Dog"
  id: string;
  timestamp: number; // This is the end timestamp
  startTime?: number; // Added optional startTime
  startWord: string;
  targetWord: string; // Changed from targetWord to targetWord for consistency
  status: "won" | "given_up";
  pathEfficiency: number;
  // Challenge flags
  isChallenge?: boolean; // Flag to identify regular challenge games (shared links)
  isDailyChallenge?: boolean; // Flag to identify daily challenge games
  isTutorial?: boolean; // Flag to identify the guided tutorial game (the first game; mode "tutorial")
  dailyChallengeId?: string; // Store the daily challenge ID directly
  aiPath?: string[]; // AI solution path for daily challenges
  aiModel?: string | null; // AI model that generated the solution
  ariadne?: AriadneReport;
  /** Every game she learned from (D10): the record's ahead + left_behind after this game. */
  ariadneGamesAhead?: number;
}

/** A game's mode. The tutorial is a first-class mode like the others. */
export type GameMode = "tutorial" | "daily" | "challenge" | "random";

/** Classify a game by its flags. Precedence: tutorial > daily > challenge > random. */
export const gameModeOf = (
  flags: Pick<GameReport, "isTutorial" | "isDailyChallenge" | "isChallenge">,
): GameMode => {
  if (flags.isTutorial) return "tutorial";
  if (flags.isDailyChallenge) return "daily";
  if (flags.isChallenge) return "challenge";
  return "random";
};

/** History badge label per mode; random games carry no badge. */
export const GAME_MODE_LABELS: Record<GameMode, string | null> = {
  tutorial: "Tutorial",
  daily: "Daily",
  challenge: "Challenge",
  random: null,
};

/**
 * Build a game report. `goldPath` is the gold path fixed at game start;
 * `bestRouteFn` gives the best route from the final word (the Local Optimal
 * route shown after a give-up).
 */
export const generateGameReport = (
  graphData: GraphData,
  playerPath: string[],
  goldPath: string[],
  moveJudgements: MoveJudgement[],
  targetWord: string,
  bestRouteFn: RoutePathFn,
  backtrackEvents: BacktrackReportEntry[],
  potentialRarestMovesInput?: PotentialRarestMove[], // Added parameter
  // Challenge flags
  isDailyChallenge?: boolean,
  dailyChallengeId?: string, // Add dailyChallengeId parameter
  aiPath?: string[],
  aiModel?: string | null,
  isChallenge?: boolean, // Add isChallenge parameter
  isTutorial?: boolean,
): GameReport => {
  const playerSemanticDistanceTotal = calculatePathDistance(
    graphData,
    playerPath,
  );

  const goldDistance = calculatePathDistance(graphData, goldPath);
  const totalMoves = playerPath.length - 1;
  const optimalMovesMade = moveJudgements.filter(
    (oc) => oc.isGlobalOptimal || oc.isLocalOptimal,
  ).length;
  const moveAccuracy =
    totalMoves > 0 ? Math.min(100, (optimalMovesMade / totalMoves) * 100) : 0;
  const averageSimilarity = calculateAverageSimilarity(graphData, playerPath);

  const localOptimalRouteFromEnd = bestRouteFn(
    graphData,
    playerPath[playerPath.length - 1],
    targetWord,
  );
  const gameStatus: GameReport["status"] =
    playerPath[playerPath.length - 1] === targetWord ? "won" : "given_up"; // Assuming 'lost' is not used or handled elsewhere

  // Calculate metrics (simplified for brevity, assuming they exist)
  const pathEfficiency =
    goldPath.length > 1 && playerPath.length > 1
      ? (goldPath.length - 1) / (playerPath.length - 1)
      : 0;

  const missedOptimalMovesList: string[] = [];
  for (const choice of moveJudgements) {
    if (goldPath.includes(choice.playerPosition)) {
      const goldNext = goldPath[goldPath.indexOf(choice.playerPosition) + 1];
      if (goldNext && choice.playerChose !== goldNext) {
        missedOptimalMovesList.push(
          `At ${choice.playerPosition}, chose ${choice.playerChose} instead of the gold-path word ${goldNext}`,
        );
      }
    }
  }

  // A won game needs no Local Optimal route from its final word.
  const finalSuggestedPath =
    gameStatus === "won" ? [] : localOptimalRouteFromEnd;

  return {
    id: Date.now().toString(),
    timestamp: Date.now(),
    startTime: Date.now(),
    startWord: playerPath[0],
    targetWord,
    playerPath: playerPath,
    optimalPath: goldPath,
    suggestedPath: finalSuggestedPath,
    optimalChoices: moveJudgements,
    status: gameStatus,
    totalMoves,
    optimalMovesMade,
    moveAccuracy,
    missedOptimalMoves: missedOptimalMovesList,
    playerSemanticDistance: playerSemanticDistanceTotal,
    optimalSemanticDistance: goldDistance,
    averageSimilarity,
    backtrackEvents: backtrackEvents.length > 0 ? backtrackEvents : undefined,
    earnedAchievements: [],
    pathEfficiency: pathEfficiency,
    potentialRarestMoves: potentialRarestMovesInput, // Assign to report
    isChallenge, // Include challenge flag
    isDailyChallenge,
    isTutorial,
    dailyChallengeId,
    aiPath,
    aiModel,
  };
};

export type { GraphData } from "../services/dataLoader";
